/**
 * usePageTracking — Professional client-side analytics tracker
 *
 * Modelled after how Amplitude / Segment / Mixpanel work:
 *  1. Persistent anonymous user ID (localStorage) — survives sessions
 *  2. Session ID (sessionStorage) — resets when tab closes, like GA sessions
 *  3. First-visit "new user" flag (localStorage) — set once, never reset
 *  4. Pageview event on every route change
 *  5. Scroll-depth milestones (25 / 50 / 75 / 100 %) per page
 *  6. Device type detection (mobile / tablet / desktop)
 *
 * Events are written to Firestore → `analyticsEvents` collection.
 * Failures are swallowed silently so analytics never breaks the UX.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

// ── Route → Human-readable page name ─────────────────────────────────────────
const PAGE_LABELS = {
  '/':                 'Home',
  '/FoodMenu':         'Menu',
  '/Menu':             'Menu',
  '/Catering':         'Catering',
  '/account':          'My Account',
  '/clock':            'Clock In/Out',
  '/shifts':           'Shift Dashboard',
  '/tasks':            'Tasks',
  '/admin':            'Manage Users',
  '/login':            'Login',
  '/signup':           'Sign Up',
  '/daily-ops':        'Daily Operations',
};

const SCROLL_MILESTONES = [25, 50, 75, 100];

// ── Identity helpers ──────────────────────────────────────────────────────────

/** Persistent anonymous user ID — one per browser profile, never changes */
function getAnonId() {
  const KEY = 'db_anon_id';
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem(KEY, id);
  }
  return id;
}

/**
 * Session ID — resets every time a user opens a new tab or closes the browser.
 * Matches the GA / Amplitude session model.
 */
function getSessionId() {
  const KEY = 'db_session_id';
  let id = sessionStorage.getItem(KEY);
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(KEY, id);
  }
  return id;
}

/**
 * Returns true the very first time a user visits (ever).
 * After that, sets a localStorage flag and always returns false.
 */
function checkIsNewUser() {
  const KEY = 'db_returning';
  if (!localStorage.getItem(KEY)) {
    localStorage.setItem(KEY, '1');
    return true;
  }
  return false;
}

// ── Device detection ──────────────────────────────────────────────────────────

function getDevice() {
  const w = window.innerWidth;
  if (w < 768)  return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg'))  return 'Chrome';
  if (ua.includes('Firefox'))                          return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg'))                              return 'Edge';
  return 'Other';
}

// ── Scroll helpers ────────────────────────────────────────────────────────────

function getScrollPct() {
  const el    = document.documentElement;
  const total = el.scrollHeight - el.clientHeight;
  if (total <= 0) return 100;
  return Math.round((window.scrollY / total) * 100);
}

// ── Firestore writer ──────────────────────────────────────────────────────────

async function logEvent(payload) {
  try {
    await addDoc(collection(db, 'analyticsEvents'), payload);
  } catch {
    // Never surface analytics errors to the user
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePageTracking() {
  const location  = useLocation();
  const { user }  = useAuth();

  // Stable refs — computed once per browser session
  const sessionId = useRef(getSessionId());
  const anonId    = useRef(getAnonId());
  const isNewUser = useRef(checkIsNewUser());

  useEffect(() => {
    const page      = location.pathname;
    const pageLabel = PAGE_LABELS[page] || page;
    const now       = new Date();
    // Use local date (not UTC) so "Today" matches the user's clock/timezone
    const date      = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
    const hour      = now.getHours();

    // Fired scroll milestones reset on every page change
    const firedMilestones = new Set();

    const basePayload = {
      sessionId:       sessionId.current,
      userId:          user?.uid || anonId.current,
      userName:        user ? (localStorage.getItem('db_user_name') || user.displayName || user.email || user.uid) : null,
      isAuthenticated: !!user,
      isNewUser:       isNewUser.current,
      device:          getDevice(),
      browser:         getBrowser(),
      page,
      pageLabel,
      date,
      hour,
      timestamp:       Timestamp.now(),
    };

    // Log the pageview immediately
    logEvent({ type: 'pageview', ...basePayload });

    // Scroll depth — fire once per milestone per page
    const handleScroll = () => {
      const pct = getScrollPct();
      for (const milestone of SCROLL_MILESTONES) {
        if (pct >= milestone && !firedMilestones.has(milestone)) {
          firedMilestones.add(milestone);
          logEvent({
            type:        'scroll_depth',
            scrollDepth: milestone,
            ...basePayload,
            timestamp:   Timestamp.now(),
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);

  // Re-run whenever the route or auth state changes
  }, [location.pathname, user]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * Thin wrapper component — drop inside <Router> to activate tracking.
 * Returns null so it renders nothing.
 */
export default function PageTracker() {
  usePageTracking();
  return null;
}
